<template>
  <v-container fluid class="atch-page">
    <AError :api="apiUpdateAttachment" />

    <!-- ===== Header ===== -->
    <div class="atch-header">
      <div class="atch-header__lead">
        <button class="atch-back" @click="redirectBack()" title="Back to Settings">
          <!-- Heroicon: arrow-left -->
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="atch-icon-18">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
        </button>
        <div class="atch-header__text">
          <h1 class="atch-header__title">{{ model.name.singular }} Settings</h1>
          <p class="atch-header__subtitle">
            Grant application guideline &amp; flyer served for each {{ name1 }} pricing tier
          </p>
        </div>
      </div>

      <div class="atch-header__actions">
        <span v-if="!canEdit" class="atch-pill atch-pill--muted">Read only</span>

        <template v-else>
          <span class="atch-pill" :class="changeCount ? 'atch-pill--pending' : 'atch-pill--muted'">
            {{ changeCount ? changeCount + ' pending change' + (changeCount > 1 ? 's' : '') : 'No pending changes' }}
          </span>

          <button
            class="atch-btn atch-btn--ghost"
            :disabled="!changeCount"
            @click="discardAll()"
          >
            Discard
          </button>

          <button
            class="atch-btn atch-btn--primary"
            :disabled="!changeCount"
            @click="toggleUpdateAtchDialog(true)"
          >
            <!-- Heroicon: arrow-up-tray -->
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="atch-icon-16">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
            </svg>
            Save changes
          </button>
        </template>
      </div>
    </div>

    <!-- ===== Alert errors ===== -->
    <AAlertBanner :messages="alertError" title="Warning" icon="mdi-alert" />

    <!-- ===== Upload hint ===== -->
    <UploadAttachmentSettingFileSizeAlert></UploadAttachmentSettingFileSizeAlert>

    <!-- ===== Pricing tier cards ===== -->
    <div class="tier-grid">
      <section
        v-for="price in staticTaxpodPricing"
        :key="price"
        class="tier-card"
        :class="{ 'tier-card--changed': isTierChanged(price) }"
      >
        <header class="tier-card__header">
          <div class="tier-card__price">
            <span class="tier-card__currency">RM</span>
            <span class="tier-card__amount">{{ price.toLocaleString() }}</span>
          </div>
          <span v-if="isTierChanged(price)" class="tier-card__badge">Pending</span>
        </header>

        <div class="tier-card__body">
          <template v-for="(doc, docIndex) in staticTaxpodDocs">
            <div
              :key="doc"
              class="doc"
              :class="{
                'doc--drag': dragKey === price + '-' + doc,
                'doc--staged': isStaged(price, doc),
                'doc--error': !!errorField[price][doc],
              }"
              @dragenter.prevent="onDragEnter(price, doc)"
              @dragover.prevent="onDragEnter(price, doc)"
              @dragleave.prevent="onDragLeave($event)"
              @drop.prevent="addDropFile($event, price, doc)"
            >
              <div class="doc__icon">
                <!-- Heroicon: document-text -->
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="atch-icon-20">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>

              <div class="doc__info">
                <span class="doc__label">
                  {{ docLabel(doc) }}
                  <span v-if="isStaged(price, doc)" class="doc__tag">New</span>
                </span>
                <span class="doc__name" :title="fileName(price, doc)">{{ fileName(price, doc) }}</span>
                <span v-if="isStaged(price, doc)" class="doc__meta">{{ fileSize(price, doc) }}</span>
              </div>

              <div class="doc__actions">
                <v-tooltip top v-if="canEdit && isStaged(price, doc)">
                  <template v-slot:activator="{ on }">
                    <button class="doc-btn" @click="discardFile(price, doc)" v-on="on">
                      <!-- Heroicon: arrow-uturn-left -->
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="atch-icon-16">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                      </svg>
                    </button>
                  </template>
                  <span>Discard selection</span>
                </v-tooltip>

                <v-tooltip top v-if="canEdit">
                  <template v-slot:activator="{ on }">
                    <label class="doc-btn doc-btn--upload" :for="'file-' + price + '-' + doc" v-on="on">
                      <!-- Heroicon: paper-clip -->
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="atch-icon-16">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
                      </svg>
                      <input
                        type="file"
                        :id="'file-' + price + '-' + doc"
                        class="doc__input"
                        accept="application/pdf,.pdf"
                        @change="handleFileSelect($event, price, doc)"
                      />
                    </label>
                  </template>
                  <span>Choose PDF</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <button class="doc-btn" @click="previewAttachment(formData[price][doc])" v-on="on">
                      <!-- Heroicon: eye -->
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="atch-icon-16">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </button>
                  </template>
                  <span>Preview file</span>
                </v-tooltip>
              </div>

              <div v-if="dragKey === price + '-' + doc" class="doc__dropzone">Drop PDF to replace</div>
            </div>

            <p v-if="errorField[price][doc]" :key="doc + '-err'" class="doc__error">
              {{ errorField[price][doc] }}
            </p>

            <div
              v-if="docIndex < staticTaxpodDocs.length - 1"
              :key="doc + '-divider'"
              class="tier-card__divider"
            ></div>
          </template>
        </div>
      </section>
    </div>

    <TheModalConfirmation
      ref="updateAtchConfirmPopup"
      @agree="agreeCallbacUpdateAtch()"
      @disagree="disagreeCallbacUpdateAtch()"
    ></TheModalConfirmation>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";
import UploadAttachmentSettingFileSizeAlert from "@/components/views/UploadAttachmentSettingFileSizeAlert.vue";

export default {
  components: {
    TheModalConfirmation,
    UploadAttachmentSettingFileSizeAlert,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    canEdit() {
      return this.isHrdcSuperAdminRoles;
    },
    // Number of files staged for upload across every tier.
    changeCount() {
      if (!this.formData) return 0;

      return Object.values(this.formData).reduce((total, docs) => {
        return (
          total +
          this.staticTaxpodDocs.filter((doc) => docs[doc] instanceof File).length
        );
      }, 0);
    },
  },
  props: ["params"],
  data: () => ({
    apiUpdateAttachment: new Api(),
    apiGetAttachmentBlob: new Api(),
    model: new Model(),
    service: new Service(),
    alertError: [],
    formData: null,
    errorField: null,
    loadingDialog: false,
    dragKey: null,
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.serviceDataAssignation(this.service.key);

    const apiDetails = {
      apiGetAttachmentBlob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-attachment-blob`,
      },
      apiUpdateAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/attachment/update-attachment`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].query !== undefined) {
        this[api].setQuery(apiDetails[api].query);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    // Assign the currently published (static) file names per tier.
    for (const price of this.staticTaxpodPricing) {
      const docs = {};
      const errors = {};

      for (const doc of this.staticTaxpodDocs) {
        docs[doc] = { name: this.getOriFileName(price, doc) };
        errors[doc] = null;
      }

      this.formData = { ...this.formData, [price]: docs };
      this.errorField = { ...this.errorField, [price]: errors };
    }

    this.rolesAssignation(this.auth.roles);
  },
  methods: {
    docLabel(doc) {
      return this.capitalizeFirstLetter(doc);
    },
    isStaged(price, doc) {
      return this.formData[price][doc] instanceof File;
    },
    isTierChanged(price) {
      return this.staticTaxpodDocs.some((doc) => this.isStaged(price, doc));
    },
    fileName(price, doc) {
      const file = this.formData[price][doc];
      return file ? file.name : "No file selected";
    },
    fileSize(price, doc) {
      const file = this.formData[price][doc];
      if (!(file instanceof File)) return null;

      const kb = file.size / 1024;
      return kb < 1024
        ? `${kb.toFixed(0)} KB`
        : `${(kb / 1024).toFixed(2)} MB`;
    },
    stageFile(price, doc, file) {
      if (!file) return;

      if (!file.type.match("pdf.*")) {
        this.$set(this.errorField[price], doc, "Please select pdf file format");
        return;
      }

      this.$set(this.errorField[price], doc, null);
      this.$set(this.formData[price], doc, file);
    },
    handleFileSelect(event, price, doc) {
      this.stageFile(price, doc, event.target.files[0]);
      // Allow re-picking the same file straight after a rejected upload.
      event.target.value = "";
    },
    onDragEnter(price, doc) {
      if (!this.canEdit) return;

      this.dragKey = `${price}-${doc}`;
    },
    onDragLeave(event) {
      // dragleave also fires when the cursor crosses onto a child of the row —
      // only drop the highlight once it has actually left the row itself.
      if (event.currentTarget.contains(event.relatedTarget)) return;

      this.dragKey = null;
    },
    addDropFile(event, price, doc) {
      this.dragKey = null;
      if (!this.canEdit) return;

      this.stageFile(price, doc, event.dataTransfer.files[0]);
    },
    discardFile(price, doc) {
      this.$set(this.formData[price], doc, {
        name: this.getOriFileName(price, doc),
      });
      this.$set(this.errorField[price], doc, null);
    },
    discardAll() {
      for (const price of this.staticTaxpodPricing) {
        for (const doc of this.staticTaxpodDocs) {
          this.discardFile(price, doc);
        }
      }
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        for (const doc of this.staticTaxpodDocs) {
          this.errorField[error][doc] = null;
        }
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    redirectBack() {
      let route = this.model.parent_route;
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
      });
    },
    previewAttachment(fileType) {
      // Preview for static file.
      // Check is empty file.
      if (!fileType) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
          timeout: 2000,
        });
        return false;
      }

      // If user has selected a new file, preview the local file directly.
      if (fileType instanceof File) {
        this.previewBlobAttachment(fileType);
        return;
      }

      this.showLoadingDialog();

      const fileUrl = `assets/storage/${fileType.name}`;

      this.$axios
        .get(this.apiGetAttachmentBlob.url, {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: fileUrl,
          },
        })
        .then((response) => {
          const blobUrl = URL.createObjectURL(
            new Blob([response.data], { type: "application/pdf" })
          );

          window.open(blobUrl, "_blank");
        })
        .catch(() => {
          this.previewBlobAttachment(fileType);
        })
        .finally(() => {
          this.hideLoadingDialog();
        });
    },
    async previewBlobAttachment(fileType) {
      // Preview for newly attached file.
      this.showLoadingDialog();

      if (!fileType) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
        });
        this.hideLoadingDialog();
        return;
      }

      let url = null;
      let newWindow = null;
      switch (fileType) {
        default:
          url = URL.createObjectURL(fileType);
          newWindow = window.open(url, "_blank");
          break;
      }

      if (newWindow) {
        newWindow.onload = function () {
          // Revoke the object URL when the window is closed
          newWindow.addEventListener("beforeunload", function () {
            URL.revokeObjectURL(url);
          });
        };
      }

      this.hideLoadingDialog();
    },
    getOriFileName(price, file) {
      let fileName = null;

      switch (file) {
        case "guideline":
          fileName = `${this.guidelineName}${price}.pdf`;
          break;

        case "flyer":
          fileName = `${this.flyerName}${price}.pdf`;
          break;
      }

      return fileName;
    },
    toggleUpdateAtchDialog(val) {
      if (val) {
        this.$refs.updateAtchConfirmPopup.open({
          title: "Update Attachment",
          message: "Are you confirm to update attachment?",
          options: {
            btnDisagree: "Cancel",
            btnAgree: "Update",
          },
          agree: () => {
            this.agreeCallbacUpdateAtch();
          },
          disagree: () => {
            this.disagreeCallbacUpdateAtch();
          },
        });
      }
    },
    agreeCallbacUpdateAtch() {
      this.showLoadingDialog();
      this.clearErrorMsg();

      // Check is file fields empty.
      let isUploadContainsEmpty = false;

      Object.entries(this.formData).forEach(([field, value]) => {
        this.staticTaxpodDocs.forEach((key) => {
          if (!value[key]) {
            this.errorField[field][key] = `${this.capitalizeFirstLetter(
              key
            )} ${field} is required`;
            isUploadContainsEmpty = true;
          }
        });
      });

      if (isUploadContainsEmpty) {
        this.hideLoadingDialog();
        return false;
      }

      const fd = new FormData();

      // Only append File instance.
      Object.entries(this.formData).forEach(([atch, data]) => {
        this.staticTaxpodDocs.forEach((key) => {
          if (data[key] instanceof File) {
            fd.append(
              `${atch}_${key}_filepath`,
              `assets/storage/${this.getOriFileName(atch, key)}`
            );
            fd.append(`${atch}_${key}`, data[key]);
          }
        });
      });

      const fdLength = [...fd.entries()].length;
      if (fdLength == 0) {
        this.$store.dispatch("showMessage", {
          message: "No changes apply!",
          messageType: "info",
          timeout: 2000,
        });

        this.hideLoadingDialog();
        return;
      }

      // Only append static datas after checking.
      fd.append("staticFiles", this.staticTaxpodDocs);
      fd.append("staticPrice", this.staticTaxpodPricing);

      this.apiUpdateAttachment.setParams(fd);
      this.apiUpdateAttachment.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;

          let messageType = "success";
          if (!status) {
            messageType = "errror";
          }

          this.$store.dispatch("showMessage", {
            message: data,
            messageType,
            timeout: 2000,
          });

          // Staged files are published now — fall back to the static names.
          if (status) this.discardAll();
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiUpdateAttachment.fetch();
    },
    disagreeCallbacUpdateAtch() {
      //
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored, token references only (--app-*, defined in
   src/plugins/theme-dark.css). No literal colours here — a palette change
   happens in that one file and this page follows.

   Same system as the parent Settings index: the surface ramp does the
   separating (page → card → tile), the edge is --app-border instead of the
   light theme's near-white hairline, and depth is shadow + a 1px inner top
   highlight rather than a heavier drop shadow.
   ============================================================================ */

.dark-mode .atch-page {
  background-color: var(--app-page);
  background-image: var(--app-page-glow);
}

.dark-mode .atch-header__title {
  color: var(--app-text);
}

.dark-mode .atch-header__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .atch-back {
  background: var(--app-card);
  border-color: var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .atch-back:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .atch-pill--muted {
  background: var(--app-neutral-bg);
  color: var(--app-text-muted);
}

.dark-mode .atch-pill--pending {
  background: var(--app-warning-bg);
  color: var(--app-warning-fg);
}

.dark-mode .atch-btn--ghost {
  background: var(--app-card);
  border-color: var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .atch-btn--ghost:hover:not(:disabled) {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .atch-btn--primary {
  background: var(--app-accent);
  color: var(--app-accent-fg);
  box-shadow: 0 2px 10px var(--app-accent-glow);
}

.dark-mode .atch-btn--primary:hover:not(:disabled) {
  background: var(--app-accent-strong);
}

.dark-mode .tier-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 4px 14px var(--app-shadow-mid);
}

.dark-mode .tier-card:hover {
  border-color: var(--app-border-strong);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 8px 22px var(--app-shadow);
}

.dark-mode .tier-card--changed {
  border-color: var(--app-accent-border);
}

.dark-mode .tier-card__header {
  border-bottom-color: var(--app-border);
}

.dark-mode .tier-card__currency {
  color: var(--app-text-faint);
}

.dark-mode .tier-card__amount {
  color: var(--app-text);
}

.dark-mode .tier-card__badge {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

.dark-mode .tier-card__divider {
  background: var(--app-border);
}

.dark-mode .doc {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .doc:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .doc--staged {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
}

.dark-mode .doc--error {
  border-color: var(--app-danger-border);
}

.dark-mode .doc--drag {
  border-color: var(--app-accent);
  background: var(--app-accent-soft);
}

.dark-mode .doc__icon {
  background: var(--app-danger-bg);
  color: var(--app-danger);
}

.dark-mode .doc__label {
  color: var(--app-text-muted);
}

.dark-mode .doc__tag {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

.dark-mode .doc__name {
  color: var(--app-text);
}

.dark-mode .doc__meta {
  color: var(--app-text-faint);
}

.dark-mode .doc-btn {
  background: var(--app-card);
  border-color: var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .doc-btn:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .doc-btn--upload:hover {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
  color: var(--app-accent-strong);
}

.dark-mode .doc__dropzone {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
  border-color: var(--app-accent);
}

.dark-mode .doc__error {
  color: var(--app-danger);
}

/* ===== end DARK MODE ===== */

/* ============================================
   PAGE
   ============================================ */
.atch-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.atch-icon-16 {
  width: 16px;
  height: 16px;
}

.atch-icon-18 {
  width: 18px;
  height: 18px;
}

.atch-icon-20 {
  width: 20px;
  height: 20px;
}

/* ============================================
   HEADER
   ============================================ */
.atch-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.atch-header__lead {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.atch-back {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.9);
  color: #64748b;
  cursor: pointer;
  transition: all 0.18s ease;
}

.atch-back:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.atch-header__text {
  min-width: 0;
}

.atch-header__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
  letter-spacing: -0.2px;
}

.atch-header__subtitle {
  font-size: 0.88rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

.atch-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 4px;
}

/* Status pill */
.atch-pill {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
}

.atch-pill--muted {
  background: #e6e9ee;
  color: #64748b;
}

.atch-pill--pending {
  background: #fef3c7;
  color: #b45309;
}

/* Buttons */
.atch-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.83rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.atch-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.atch-btn--ghost {
  background: #ffffff;
  border-color: rgba(226, 232, 240, 0.9);
  color: #64748b;
}

.atch-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.atch-btn--primary {
  background: #0ea5e9;
  color: #ffffff;
  box-shadow: 0 2px 10px rgba(14, 165, 233, 0.28);
}

.atch-btn--primary:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
}

/* ============================================
   TIER CARDS
   ============================================ */
.tier-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 18px;
}

.tier-card {
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.tier-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.07);
}

.tier-card--changed {
  border-color: #7dd3fc;
}

.tier-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.tier-card__price {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.tier-card__currency {
  font-size: 0.8rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.tier-card__amount {
  font-size: 1.45rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.5px;
}

.tier-card__badge {
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
}

.tier-card__body {
  padding: 16px 18px;
}

.tier-card__divider {
  height: 1px;
  background: #f1f5f9;
  margin: 12px 0;
}

/* ============================================
   DOC ROW
   ============================================ */
.doc {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 11px 13px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  transition: all 0.15s ease;
}

.doc:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.doc--staged {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.doc--error {
  border-color: #fecaca;
}

.doc--drag {
  border-color: #0ea5e9;
  border-style: dashed;
  background: #f0f9ff;
}

.doc__icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  color: #ef4444;
}

.doc__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.doc__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.doc__tag {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e0f2fe;
  color: #0369a1;
}

.doc__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doc__meta {
  font-size: 0.72rem;
  font-weight: 500;
  color: #94a3b8;
}

.doc__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.doc__input {
  display: none;
}

.doc-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.doc-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

.doc-btn--upload:hover {
  background: #f0f9ff;
  border-color: #bae6fd;
  color: #0369a1;
}

/* Drag overlay */
.doc__dropzone {
  position: absolute;
  inset: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: #e0f2fe;
  color: #0369a1;
  border: 1px dashed #0ea5e9;
  pointer-events: none;
}

.doc__error {
  margin: 6px 0 0 51px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #ef4444;
}

/* ============================================
   TRANSITION
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (min-width: 600px) and (max-width: 959px) {
  .atch-page {
    padding: 24px 20px !important;
  }

  .tier-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 599px) {
  .atch-page {
    padding: 20px 14px !important;
    overflow-x: hidden;
  }

  .atch-header {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .atch-header__title {
    font-size: 1.2rem;
  }

  .atch-header__actions {
    flex-wrap: wrap;
    padding-top: 0;
  }

  .atch-btn {
    flex: 1;
    justify-content: center;
  }

  .tier-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .tier-card__body {
    padding: 14px;
  }

  .doc {
    flex-wrap: wrap;
  }

  .doc__info {
    flex: 1 1 calc(100% - 51px);
  }

  .doc__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .doc__error {
    margin-left: 0;
  }
}
</style>
